<template>
	<!-- NOTE FOR DEV:  This modal could be used for New Code, Copy of Code and Code Settings, so must need only change the title. -->
	<!-- I move the modal from the footer to this slot, but don't load the
		"code.id" data -->
	<Modals
		:title="`${calcActionMode} Code`"
		:validateBeforeClose="validateBeforeClose"
		:display="display"
		@show="close"
		:parentTag="'form'"
		:novalidate="'novalidate'"
		:fullMobile="true"
	>
		<template #modal-content>
			<div class="snippet-details">
				<!-- NOTE FOR DEV:  The notification block should show when the form is in edit mode. Please see confluence for details -->
				<BlockContainer>
					<BlockNotifications
						title="Code in use on Form"
						:message="
							`${code.description} is used on other forms. Any edits will be applied to ALL forms once any containing this code is published.`
						"
						v-if="shouldShowBlockNotifications"
					/>
				</BlockContainer>
				<div class="snippet-details__code-id_description">
					<div class="snippet-details__id-item">
						<Input
							className="field__input"
							label="Code ID"
							:required="false"
							:disabled="true"
							:value="code.id"
						/>
					</div>

					<div class="snippet-details__description-item">
						<Input
							className="field__input"
							label="Code Description"
							name="codeDescription"
							:maxLength="500"
							:required="true"
							v-model="code.description"
							@hasError="handleError"
						/>
					</div>
				</div>

				<div class="snippet-details__enter-code-item">
					<Textarea
						label="Code"
						:rows="4"
						:cols="30"
						v-model="code.snippetCalc"
						:required="true"
						:maxLength="4000"
						name="codeCalc"
						@hasError="handleError"
					/>
				</div>

				<div class="snippet-details__code-attribute_format">
					<div class="snippet-details__attribute-item">
						<Dropdown
							label="Attribute"
							:defaultValue="'Select one'"
							:required="false"
							:options="ddAttributeOptions"
							v-model="code.snippetAttribute"
						/>
					</div>
					<div class="snippet-details__format-item">
						<Input
							className="field__input"
							label="Format"
							v-model="code.snippetFormat"
							:required="false"
							:maxLength="15"
						/>
					</div>
				</div>

				<div class="snippet-details__justification-item">
					<Fieldset
						:legend="'Justification'"
						variant="fieldset--no-borders"
					>
						<template slot="fieldset-content">
							<RadioButtonGroup
								:items="radiobuttonItems"
								v-model="code.snippetJustify"
							/>
						</template>
					</Fieldset>
				</div>

				<div class="snippet-details__comment-item">
					<Textarea
						label="Comment"
						placeholder="Add comment about the reason for the code"
						:rows="4"
						:cols="30"
						v-model="code.message"
						:required="commentRequired"
						:maxLength="200"
						name="codeComment"
						@hasError="handleError"
					/>
				</div>
			</div>
			<Modals
				v-model="showModal"
				type="confirmation"
				:title="confirmationTitle"
				labelConfirmButton="Yes, remove the code"
				labelCancelButton="Cancel"
				@confirm="deleteAction"
				@cancel="cancel"
			/>
		</template>

		<!-- DEV NOTES:  For Code Settings modal:
		-- the btn--primary labelText needs to change to "Save Changes"
		-->

		<template #modal-footer>
			<div class="modal-code__actions">
				<Button
					labelText="Delete"
					className="btn--has-icon"
					iconClassName="icon-delete-glenn"
					:iconBeforeLabel="true"
					:btnClick="
						() => {
							showModalRemoveCode(code);
						}
					"
					v-show="shouldShowDeleteButton"
				/>

				<!-- NOTE FOR DEV:  For Code Settings modal the btn--primary labelText needs to change to "Save Changes". -->
				<ButtonGroup
					:items="[
						{
							className: 'btn--primary',
							labelText: 'Save Code',
							btnClick: saveAction,
							disabled: disableSaveCode
						},
						{
							className: 'btn--link',
							labelText: 'Cancel',
							btnClick: close
						}
					]"
				/>
			</div>
		</template>
	</Modals>
</template>

<script>
import Modals from '@/components/shells/modal/modals';
import Input from '@/components/forms/input/input';
import Textarea from '@/components/forms/textarea/textarea.vue';
import Fieldset from '@/components/forms/fieldset/fieldset';
import RadioButtonGroup from '@/components/forms/selection-control/radiobutton/radiobutton-group';
import ButtonGroup from '@/components/forms/button/button-group';
import Button from '@/components/forms/button/button';
import Dropdown from '@/components/forms/dropdown/dropdown.vue';
import { mapGetters } from 'vuex';
import SnippetService from '@/services/snippet-service';
import { isObject } from '@/utils/inspect';
import { keys, deepEqual } from '@/utils/object.js';
import BlockContainer from '@/components/notifications/block-notifications/block-notifications-container';
import BlockNotifications from '@/components/notifications/block-notifications/block-notifications';
import _ from 'lodash';
import { attributeOptions } from '@/views/shared/snippet-attribute.js';

export default {
	name: 'NewCopyOfCodeSettingsModal',
	components: {
		Modals,
		Input,
		Textarea,
		Fieldset,
		RadioButtonGroup,
		ButtonGroup,
		Dropdown,
		BlockContainer,
		BlockNotifications,
		Button
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		title: {
			type: String,
			default: ''
		},
		parentTag: {
			type: String,
			default: 'div'
		},
		validateBeforeClose: {
			type: Boolean,
			default: false
		},
		display: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			privilegedUser: true,
			radiobuttonItems: [
				{
					labelText: 'Left',
					disabled: false,
					value: 'L'
				},
				{
					labelText: 'Right',
					disabled: false,
					value: 'R'
				}
			],
			code: {},
			calcActionMode: '',
			fieldsErrors: [],
			showModal: false,
			confirmationTitle: ''
		};
	},
	computed: {
		...mapGetters('codemanager', ['codeSelected']),

		isMobile() {
			return this.screenSize.mobile;
		},

		commentRequired() {
			return this.calcActionMode.toLowerCase() === 'edit';
		},
		shouldShowBlockNotifications() {
			return (
				_.get(this, 'code.id', 0) > 0 &&
				_.get(this, 'code.forms.length', 0) > 0
			);
		},
		shouldShowDeleteButton() {
			return (
				_.get(this, 'code.id', 0) > 0 &&
				_.get(this, 'code.forms.length', 0) == 0 &&
				this.code.canBeDeleted
			);
		},
		ddAttributeOptions() {
			return attributeOptions();
		},
		modelHasError() {
			if (_.isEmpty(this.codeSelected)) {
				const { description, snippetCalc } = this.code;
				return !description || !snippetCalc;
			}
			const areEqual = deepEqual(this.code, this.codeSelected);

			return this.fieldsErrors.length > 0 || areEqual;
		},
		disableSaveCode() {
			if (this.calcActionMode.toLowerCase() === 'new') {
				const { description, snippetCalc } = this.code;
				const hasEmptyRequiredValues = !description || !snippetCalc;
				return hasEmptyRequiredValues || this.modelHasError;
			} else {
				const areEqual = deepEqual(this.code, this.codeSelected);
				if (areEqual) {
					return true;
				}
				const { message } = this.code;
				if (!message) {
					return true;
				}
				return this.modelHasError;
			}
		}
	},
	methods: {
		close() {
			this.code = {};
			this.$emit('show', false);
			this.showModal = false;
		},
		updateToast(val) {
			this.$store.dispatch('toastMessages/show', {
				toastId: 'snippet.action',
				statusCode: val.status,
				message: val.message,
				slot: 'default',
				useIcon: true,
				userDismissable: true
			});
		},
		async saveAction() {
			const self = this;
			if (self.code.id) {
				await SnippetService.UpdateSnippet(this.code)
					.then(response => {
						let snippet = Object.assign(
							{},
							{
								description: response.description,
								forms: response.forms,
								id: response.id,
								originalFormsSnippetId:
									response.originalFormsSnippetId,
								snippetAttribute: response.snippetAttribute,
								snippetCalc: response.snippetCalc,
								snippetFormat: response.snippetFormat,
								snippetJustify: response.snippetJustify,
								sourceOfSelection: 'UpdateSnippet'
							}
						);
						self.$store.dispatch(
							'codemanager/codeSelected',
							snippet
						);
						self.$store.dispatch('codemanager/reloadCodeManager');
						self.close();
						self.updateToast({
							status: response.statusCode,
							message: 'Code successfully updated.'
						});
					})
					.catch(error => {
						self.close();
						self.updateToast({
							status: 400,
							message: error
								? error
								: 'Code unsuccessfully updated.'
						});
					});
			} else {
				await SnippetService.SaveSnippet(this.code)
					.then(response => {
						let snippet = Object.assign(
							{},
							{
								description: response.description,
								forms: response.forms,
								id: response.id,
								originalFormsSnippetId:
									response.originalFormsSnippetId,
								snippetAttribute: response.snippetAttribute,
								snippetCalc: response.snippetCalc,
								snippetFormat: response.snippetFormat,
								snippetJustify: response.snippetJustify,
								sourceOfSelection: 'SaveSnippet'
							}
						);
						self.$store.dispatch(
							'codemanager/codeSelected',
							snippet
						);
						self.$store.dispatch('codemanager/reloadCodeManager');
						self.close();
						self.updateToast({
							status: response.statusCode,
							message: 'Code successfully created.'
						});
					})
					.catch(error => {
						self.close();
						self.updateToast({
							status: 400,
							message: error
								? error
								: 'Code unsuccessfully created.'
						});
					});
			}
		},
		handleError(obj) {
			if (isObject(obj)) {
				var key = keys(obj)[0];
				const idx = this.fieldsErrors.findIndex(x => keys(x)[0] == key);

				if (idx > -1) {
					if (!obj[key]) {
						this.fieldsErrors.splice(idx, 1);
					}
				} else {
					if (obj[key]) {
						this.fieldsErrors.push(obj);
					}
				}
			}
		},
		async deleteAction() {
			if (!this.code.canBeDeleted) {
				return;
			}

			const self = this;
			if (self.code.id) {
				await SnippetService.DeleteSnippet(self.code.id)
					.then(() => {
						self.close();
						self.$store.dispatch('codemanager/reloadCodeManager');

						self.updateToast({
							status: 204,
							message: 'Code successfully deleted.'
						});
					})
					.catch(error => {
						self.close();
						self.updateToast({
							status: 400,
							message: error
								? error
								: 'Code unsuccessfully deleted.'
						});
					});
			}
		},
		cancel() {
			this.showModal = false;
		},
		showModalRemoveCode(code) {
			this.code = code;
			this.confirmationTitle = `Are you sure you want to remove the " ${code.id}" code?`;
			this.showModal = true;
		}
	},
	watch: {
		display: {
			handler(val) {
				if (val) {
					this.code = { ...this.codeSelected };

					let action = 'Edit';
					if (Object.keys(this.code).length === 0) {
						action = 'New';
					} else if (!this.code.id) {
						action = 'Copy of';
					}
					this.calcActionMode = action;

					this.code.snippetJustify = this.code.snippetJustify || 'L';
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * [Description]
    * Code Manager New Code Modal
    * Can use SCSS variables = TRUE
*/

.snippet-details {
	font-size: $default-font-size - 2px;
	overflow: hidden;
	@include mq($from: md) {
		width: 640px;
	}

	&__code-id_description {
		@include mq($from: md) {
			display: grid;
			grid-template-columns: minmax(0, 30%) minmax(0, 70%);
			gap: 24px;
		}
	}

	&__description-item {
		margin-top: $gutter-xl;
		@include mq($from: md) {
			margin-top: initial;
		}
	}

	&__enter-code-item {
		margin-top: $gutter-xl;
	}
	&__code-attribute_format {
		margin-top: $gutter-xl;
		@include mq($from: md) {
			display: grid;
			grid-template-columns: minmax(0, 50%) minmax(0, 50%);
			gap: 24px;
		}
	}

	&__format-item {
		margin-top: $gutter-xl;
		@include mq($from: md) {
			margin-top: initial;
		}
	}

	&__justification-item {
		margin-top: $gutter-xl;
	}
	&__comment-item {
		margin-top: $gutter-xl;
	}
}

//// STYLES WHEN THE DELETE BUTTON IS VISIBLE /////

.modal-code__actions {
	display: flex;
	justify-content: flex-end;

	.btn {
		margin-right: auto;

		.icon {
			font-size: $default-font-size - 2;
		}
	}
}
</style>
